import { getCustomDisplayName } from '@/components/form/dynamic-field/utils';
import { Icons } from '@/components/icons';
import { ENUM_FILTER, type Filter } from '@/components/table-filters/constants';
import EnumFilterButton from '@/components/table-filters/enum-filter-button';
import TableEnumFilters from '@/components/table-filters/enum-filters';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useTableFilter } from '@/hooks/use-table-filter';
import { useConfig } from '@/hooks/useConfig';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import { cn } from '@/lib/cn';
import { getListNested } from '@/requests/api/get-list-nested';
import { MAX_PAGE_SIZE } from '@/requests/constants';
import { useGetInfiniteList } from '@/requests/hooks';
import { useFetchAllIds } from '@/requests/hooks/use-fetch-all-ids';
import type { QueryParams } from '@/requests/types';
import type { BaseInstance, ModelName } from '@pigello/pigello-matrix';
import { useMutation } from '@tanstack/react-query';

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import TableSearch from '../../filtering/search';
import { getSelectItemFromModelName } from './select-items/relation-item-map';

export type RenderItemProps<T extends BaseInstance> = {
  item: T;
  onSelected: (id: string) => void;
  isSelected: boolean;
  isMultiSelect: boolean;
};

export default function SelectTable<T extends BaseInstance = BaseInstance>({
  queryParams,
  filters,
  modelName,
  className,
  filterBoxClassName,
  tableContianerClassName,
  isMultiSelect = false,
  onSelected,
  onSelectedInstance,
  selected,
  RenderItem,
  emptyTitle,
  tableId,
  isRelationField,
  nested,
  overrideUrl,
  enableGlobalFilters = false,
  TableFilters,
  maxSelectedItems,
  allowSelectAll = false,
}: {
  modelName: ModelName;
  queryParams?: QueryParams;
  tableId: string;
  RenderItem?: ({
    item,
    onSelected,
    isSelected,
    isMultiSelect,
  }: RenderItemProps<T>) => ReactNode;
  className?: string;
  filters?: Filter[];
  filterBoxClassName?: string;
  tableContianerClassName?: string;
  isMultiSelect?: boolean;
  onSelected: (id: string) => void;
  onSelectedInstance?: (instance: T) => void;
  selected?: string[];
  emptyTitle?: string;
  isRelationField?: boolean;
  nested?: Array<keyof T>;
  overrideUrl?: string;
  enableGlobalFilters?: boolean;
  maxSelectedItems?: number;
  allowSelectAll?: boolean;
  TableFilters: ({
    clientState,
    filters,
    tableId,
  }: {
    clientState?: boolean;
    filters: Filter[];
    tableId: string;
  }) => JSX.Element;
}) {
  const { search, combineFilters } = useTableFilter({
    isClient: true,
    tableId,
  });
  const resolvedFilters =
    filters ?? getSelectItemFromModelName(modelName)?.filters ?? [];
  const enumFilters = resolvedFilters.filter((f) => f.kind === ENUM_FILTER);
  const initialSelected = useRef<string[] | null>(null);
  const { config } = useConfig(modelName);
  const selectedRef = useRef(selected);
  const [hasSelectedAll, setHasSelectedAll] = useState(false);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    if (!initialSelected.current && selected?.length) {
      initialSelected.current = selected;
    }
  }, [selected]);

  const { data: alreadySelected, isLoading: isLoadingAlreadySelected } =
    useFetchAllIds<T>({
      modelName,
      ids: initialSelected.current ?? [],
      idAttr: 'id',
      nested: nested ?? getSelectItemFromModelName(modelName)?.nested ?? [],
      search,
      filters: combineFilters(queryParams?.filters ?? {}),
      enabled: !!initialSelected.current?.length,
    });

  const { data, isPending, hasNextPage, fetchNextPage, error, isFetching } =
    useGetInfiniteList<T>({
      overrideUrl,
      modelName,
      nested: nested ?? getSelectItemFromModelName(modelName)?.nested ?? [],
      queryParams: {
        ...queryParams,
        enableGlobalFilters,
        search,
        filters: combineFilters(queryParams?.filters ?? {}),
        page: 1,
        pageSize: 50,
      },
    });

  const [sentryRef] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage,
    onLoadMore: fetchNextPage,
    disabled: !!error,
    rootMargin: '0px 0px 100px 0px',
  });
  // Put already selected items on top
  const flatData = useMemo(() => {
    const alreadySelectedIds = new Set([
      ...(initialSelected.current ?? []),
      ...(alreadySelected?.list?.map((item) => item.id) ?? []),
    ]);
    return [
      ...(alreadySelected.list ?? []),
      ...(data?.pages.flatMap((page) => page.list) ?? []).filter(
        (item) => !alreadySelectedIds.has(item.id)
      ),
    ];
  }, [alreadySelected, data]);

  const renderDisplayName = (instance: T | undefined) => {
    if (!instance) return '';

    const customDisplay = getCustomDisplayName(config?.modelName, instance);

    if (customDisplay) return customDisplay;

    const displayKey = config?.displayFieldName;

    if (displayKey && instance[displayKey as keyof T] != null) {
      return instance[displayKey as keyof T]?.toString();
    }

    if (instance['customId'] != null) return instance['customId'];

    return instance['id'];
  };

  const { mutate: selectAll, isPending: isSelectingAll } = useMutation({
    mutationFn: async () => {
      const allItems: T[] = [];
      let page = 1;
      let pageAmount = 1;
      do {
        const res = await getListNested<T>({
          modelName,
          overrideUrl,
          nested: nested ?? getSelectItemFromModelName(modelName)?.nested ?? [],
          queryParams: {
            ...queryParams,
            enableGlobalFilters,
            search,
            filters: combineFilters(queryParams?.filters ?? {}),
            page,
            pageSize: MAX_PAGE_SIZE,
          },
        });
        allItems.push(...res.list);
        pageAmount = res.meta?.page_amount ?? 1;
        page++;
      } while (page <= pageAmount);
      return allItems;
    },
    onSuccess: (allItems) => {
      for (const item of allItems) {
        if (selectedRef.current?.includes(item.id)) continue;
        onSelected(item.id);
        if (onSelectedInstance) {
          onSelectedInstance(item);
        }
      }
      setHasSelectedAll(true);
    },
    onError: () => {
      toast.error('Kunde inte hämta alla objekt');
    },
  });

  const deselectAll = () => {
    for (const id of selected ?? []) {
      onSelected(id);
    }
    setHasSelectedAll(false);
  };

  const showDeselectAll = hasSelectedAll && (selected?.length ?? 0) > 0;

  const isSelectionDisabled = (itemId: string) => {
    return (
      isMultiSelect &&
      typeof maxSelectedItems === 'number' &&
      (selected?.length ?? 0) >= maxSelectedItems &&
      !selected?.includes(itemId)
    );
  };

  return (
    <div className={cn('flex h-[500px] flex-col', className)}>
      <div className={cn('flex flex-col gap-2 p-4', filterBoxClassName)}>
        <div className='flex flex-wrap items-center gap-2'>
          <TableSearch isClient={true} tableId={tableId} />
          {enumFilters.length > 0 && (
            <EnumFilterButton
              isFetchingTableData={isFetching}
              overrideUrl={overrideUrl}
              modelName={modelName}
              isClient={true}
              tableId={tableId}
              filters={enumFilters}
              queryParamFilters={queryParams?.filters}
              enableGlobalFilters={enableGlobalFilters}
            />
          )}
          {resolvedFilters.length > 0 && (
            <TableFilters
              filters={resolvedFilters}
              tableId={tableId}
              clientState={true}
            />
          )}
          {allowSelectAll && (
            <Button
              type='button'
              variant='outline'
              className='ml-auto'
              disabled={isSelectingAll}
              onClick={() => (showDeselectAll ? deselectAll() : selectAll())}
            >
              {isSelectingAll && (
                <Icons.loader className='size-4 animate-spin' />
              )}
              {showDeselectAll ? 'Avmarkera alla' : 'Välj alla'}
            </Button>
          )}
        </div>
        {enumFilters.length > 0 && (
          <TableEnumFilters
            isFetchingTableData={isFetching}
            overrideUrl={overrideUrl}
            modelName={modelName}
            isClient={true}
            tableId={tableId}
            filters={enumFilters}
            queryParamFilters={queryParams?.filters}
            enableGlobalFilters={enableGlobalFilters}
          />
        )}
      </div>

      <div
        className={cn(
          'h-full overflow-y-auto border-t',
          tableContianerClassName
        )}
      >
        {isPending &&
          Array.from(Array(10).keys()).map((i) => (
            <div key={i} className='flex grow flex-col gap-2'>
              <div className='flex w-full flex-col gap-2 p-4'>
                <div className='flex w-full items-center justify-between'>
                  <div className='flex items-center gap-1'>
                    <Skeleton className='h-4 w-[100px]' />
                    <Skeleton className='h-4 w-20' />
                  </div>
                  <Skeleton className='h-6 w-20' />
                </div>
                <div className='flex items-center gap-1'>
                  <Skeleton className='h-2 w-20' />
                  <Skeleton className='size-2 rounded-full' />
                  <Skeleton className='h-2 w-20' />
                  <Skeleton className='size-2 rounded-full' />
                  <Skeleton className='h-2 w-20' />
                </div>
              </div>
            </div>
          ))}

        {!isPending && !isLoadingAlreadySelected && flatData?.length === 0 && (
          <div className='flex h-full flex-col items-center justify-center gap-2 bg-surface/80 py-6'>
            <Icons.search className='size-12 opacity-50' />
            <h2 className='text-foreground'>Inga resultat</h2>
            <p>
              Vi kunde inte hitta några {emptyTitle?.toLowerCase() ?? 'objekt'}.
            </p>
          </div>
        )}

        {flatData.map((item) => {
          const isSelected = !!selected?.includes(item.id);
          if (RenderItem) {
            return (
              <RenderItem
                key={item.id}
                item={item}
                onSelected={() => {
                  onSelected(item.id);
                  if (onSelectedInstance) {
                    onSelectedInstance(item);
                  }
                }}
                isSelected={isSelected}
                isMultiSelect={isMultiSelect}
              />
            );
          }
          return (
            <div
              key={item?.id}
              role='button'
              tabIndex={-1}
              onClick={() => {
                if (isSelectionDisabled(item.id)) return;

                onSelected(item.id);
                if (onSelectedInstance) {
                  onSelectedInstance(item);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onSelected(item.id);
                }
              }}
              className='flex w-full flex-1 cursor-pointer items-center justify-between gap-4 border-b p-4 transition-colors last:border-b-0 hover:bg-accent'
            >
              <div className='flex w-full flex-row items-center gap-0.5'>
                <div
                  className={cn(
                    'mr-4',
                    !getSelectItemFromModelName(modelName).renderItem(item)
                      .avatar && 'hidden'
                  )}
                >
                  {
                    getSelectItemFromModelName(modelName).renderItem(item)
                      .avatar
                  }
                </div>
                <div>
                  <div className='mb-1 flex items-center gap-2 font-medium'>
                    {getSelectItemFromModelName(modelName)?.renderItem(item)
                      .title ?? renderDisplayName(item)}
                    {
                      getSelectItemFromModelName(modelName)?.renderItem(item)
                        .badge
                    }
                  </div>
                  <div
                    className={cn(
                      'flex w-full flex-wrap items-center gap-0.5 text-xs whitespace-nowrap',
                      isRelationField ? 'ml-6' : ''
                    )}
                  >
                    {
                      getSelectItemFromModelName(modelName)?.renderItem(
                        item,
                        isRelationField,
                        modelName
                      ).descriptionItems
                    }
                  </div>
                </div>
              </div>

              <Button
                type='button'
                onClick={(e) => {
                  e.stopPropagation();
                  onSelected(item.id);
                  if (onSelectedInstance) {
                    onSelectedInstance(item);
                  }
                }}
                className='shrink-0'
                variant={isSelected ? 'default' : 'outline'}
                disabled={isSelectionDisabled(item.id)}
              >
                {isMultiSelect
                  ? isSelected
                    ? 'Tillagd'
                    : 'Lägg till'
                  : isSelected
                    ? 'Vald'
                    : 'Välj'}
              </Button>
            </div>
          );
        })}
        {(isFetching || hasNextPage) && (
          <div
            ref={sentryRef}
            className='flex w-full items-center justify-center'
          >
            <Icons.loader className='my-4 size-4 animate-spin' />
          </div>
        )}
      </div>
    </div>
  );
}
